import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DataGridPro, dateColumn, type GridColDef } from '../../lib/dataGrid';
import GridActionLink from '../../components/GridActionLink';
import GridCrudToolbar from '../../components/GridCrudToolbar';
import {
    PINNED_ACTIONS,
    useGridPinning
} from '../../components/gridPinnedActions';
import { HISTORY_GRID_SX } from '../history/historyGridSx';
import { historyRecordRequires } from '../history/historyRecordPath';
import { useAbility } from '../auth/abilityContext';
import type { UserLinkRow } from './useUserLinks';

/**
 * The records a person is named on, laid out like the version history: which
 * kind of record, in what way the person is named on it, which record and
 * project, its day, and a link to it. Fills the box it is put in.
 */
export default function UserLinksGrid({
    rows,
    showProject = true
}: {
    rows: UserLinkRow[];
    /** Off where every row is the same project (the Grabungsmannschaft). */
    showProject?: boolean;
}) {
    const intl = useIntl();
    const ability = useAbility();
    // The link stays in reach however far the row is scrolled; nothing pinned
    // on a narrow screen (see useGridPinning).
    const pinnedColumns = useGridPinning(PINNED_ACTIONS);

    const columns = useMemo<GridColDef<UserLinkRow>[]>(() => {
        const entityTypes = [...new Set(rows.map((row) => row.entityType))];
        const kindIds = [...new Set(rows.map((row) => row.kindId))];
        return [
            {
                field: 'entityType',
                headerName: intl.formatMessage({
                    id: 'admin.users.links.entity'
                }),
                width: 170,
                type: 'singleSelect',
                valueOptions: entityTypes.map((type) => ({
                    value: type,
                    label: intl.formatMessage({ id: `history.entity.${type}` })
                }))
            },
            {
                field: 'kindId',
                headerName: intl.formatMessage({
                    id: 'admin.users.links.kind'
                }),
                width: 210,
                type: 'singleSelect',
                valueOptions: kindIds.map((kindId) => ({
                    value: kindId,
                    label: intl.formatMessage({ id: kindId })
                }))
            },
            {
                field: 'label',
                headerName: intl.formatMessage({
                    id: 'admin.users.links.record'
                }),
                flex: 1,
                minWidth: 180,
                valueGetter: (value: string) => value || '—'
            },
            ...(showProject
                ? [
                      {
                          field: 'project',
                          headerName: intl.formatMessage({
                              id: 'admin.users.links.project'
                          }),
                          width: 200,
                          valueGetter: (value: string) => value || '—'
                      }
                  ]
                : []),
            dateColumn<UserLinkRow>({
                field: 'day',
                headerName: intl.formatMessage({ id: 'admin.users.links.day' }),
                width: 120
            }),
            {
                field: 'actions',
                type: 'actions',
                headerName: '',
                width: 60,
                // Only where the record has a screen, and one the reader may
                // open (Teams ask for more than the list does).
                getActions: ({ row }) => {
                    const requires = historyRecordRequires(row.entityType);
                    if (!row.path || (requires && !ability.can(...requires))) {
                        return [];
                    }
                    return [
                        <GridActionLink
                            key="open"
                            to={row.path}
                            icon={<OpenInNewIcon />}
                            label={intl.formatMessage({
                                id: 'admin.users.links.open'
                            })}
                        />
                    ];
                }
            }
        ];
    }, [rows, showProject, intl, ability]);

    return (
        <DataGridPro
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            initialState={{
                sorting: {
                    sortModel: [{ field: 'day', sort: 'desc' }]
                },
                pinnedColumns
            }}
            showToolbar
            slots={{ toolbar: GridCrudToolbar }}
            slotProps={{ toolbar: { showQuickFilter: true } }}
            sx={HISTORY_GRID_SX}
        />
    );
}
